import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthPage from './components/AuthPage';
import Header from './components/Header';
import FinancialChart from './components/FinancialChart';
import TransactionForm from './components/TransactionForm';
import TransactionsTable from './components/TransactionsTable';
import KpiCard from './components/KpiCard';
import CategoryManager from './components/CategoryManager';
import YearSummary from './components/YearSummary';
import GoalsManager from './components/GoalsManager';
import BudgetManager from './components/BudgetManager';
import CategoryReport from './components/CategoryReport';
import FinancialCalendar from './components/FinancialCalendar';
import ImportTransactions from './components/ImportTransactions';
import BusinessManager from './components/BusinessManager';
import UserProfile from './components/UserProfile';
import CreditCards from './components/CreditCards';
import CustomReports from './components/CustomReports';
import HelpModal from './components/HelpModal';
import { exportToCSV, exportToPDF } from './utils/exportUtils';
import './App.css';

// ── Utilitário: formata nome para exibição (igual ao Header.js) ─────────────
// Ignora preposições, pega as 2 primeiras palavras principais, sem espaços, máx 18 chars
const formatDisplayName = (name) => {
  if (!name) return '';
  const stopWords = new Set(['de', 'da', 'do', 'dos', 'das', 'e', 'a', 'o', 'em', 'no', 'na', 'nos', 'nas']);
  const words = name.trim().split(/\s+/);
  const main = words.filter(w => !stopWords.has(w.toLowerCase()));
  return (main.length > 0 ? main : words).slice(0, 2).join('').substring(0, 18);
};

// ── Toast ─────────────────────────────────────────────────────────────────
const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map(t => (
      <div key={t.id} className={`toast ${t.type}`}>
        {t.type === 'success' ? '✅' : t.type === 'error' ? '❌' : 'ℹ️'} {t.msg}
      </div>
    ))}
  </div>
);

// ── Modal de Confirmação (substitui window.confirm) ────────────────────────
const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="confirm-modal" onClick={e => e.stopPropagation()}>
      <div className="confirm-icon">⚠️</div>
      <p className="confirm-message">{message}</p>
      <div className="confirm-actions">
        <button className="modal-btn modal-btn-cancel" onClick={onCancel}>Cancelar</button>
        <button className="modal-btn modal-btn-danger" onClick={onConfirm}>Confirmar</button>
      </div>
    </div>
  </div>
);

const RecurringDeleteModal = ({ onConfirmOnly, onConfirmAll, onCancel }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px' }}>
      <div className="confirm-icon" style={{ color: '#f59e0b' }}>🔁</div>
      <p className="confirm-message" style={{ fontWeight: 'bold' }}>Este lançamento é recorrente.</p>
      <p className="confirm-message">O que você deseja excluir?</p>
      <div className="confirm-actions" style={{ flexDirection: 'column', gap: '10px' }}>
        <button className="modal-btn modal-btn-danger" style={{ width: '100%' }} onClick={onConfirmAll}>
          🗑️ Excluir TODA a sequência
        </button>
        <button className="modal-btn modal-btn-confirm" style={{ width: '100%', background: '#6b7280' }} onClick={onConfirmOnly}>
          🗑️ Excluir APENAS este
        </button>
        <button className="modal-btn modal-btn-cancel" style={{ width: '100%' }} onClick={onCancel}>
          Cancelar
        </button>
      </div>
    </div>
  </div>
);


// ── Dashboard ──────────────────────────────────────────────────────────────
function Dashboard({ sessionExpired, setSessionExpired }) {
  const { authFetch, user, isAuthenticated, userAccounts, activeAccountId, setActiveAccount } = useAuth();
  const [reportData, setReportData]       = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showAddForm, setShowAddForm]     = useState(false);
  const [initialTxDate, setInitialTxDate] = useState(null);
  const [editingTx, setEditingTx]         = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [showGoals, setShowGoals]           = useState(false);
  const [showBudgets, setShowBudgets]       = useState(false);
  const [showImport, setShowImport]         = useState(false);
  const [showBusiness, setShowBusiness]     = useState(false);
  const [showProfile, setShowProfile]       = useState(false);
  const [showCards, setShowCards]           = useState(false);
  const [showCustomReports, setShowCustomReports] = useState(false);
  const [showHelp, setShowHelp]                   = useState(false);
  const [isDark, setIsDark]                 = useState(() => localStorage.getItem('wf_theme') === 'dark');
  const [toasts, setToasts]               = useState([]);
  const [confirmModal, setConfirmModal]   = useState(null); // { message, onConfirm }
  const [recurringDeleteModal, setRecurringDeleteModal] = useState(null); // { tx }
  const [pendingInvites, setPendingInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Filtros de data
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay  = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  
  const getInitials = (name, email) => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return email ? email[0].toUpperCase() : '?';
  };
  const [dateFrom, setDateFrom]     = useState(firstDay);
  const [dateTo, setDateTo]         = useState(lastDay);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterType, setFilterType] = useState('todos');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [vision, setVision] = useState(() => localStorage.getItem('wf_vision') || 'personal');
  const [showCharts, setShowCharts] = useState(false);

  // Extrai o modo e o account_id da string de visao
  const visionMode      = vision.startsWith('business') ? 'business' : 'personal';
  const visionAccountId = vision.startsWith('business:') ? vision.split(':')[1] : (activeAccountId || user?.business_id || null);

  // Persiste a visão sempre que ela mudar
  useEffect(() => {
    localStorage.setItem('wf_vision', vision);
  }, [vision]);

  const currentAccountName = useMemo(() => {
    if (vision === 'personal') return user?.name || 'Pessoal';
    if (vision.startsWith('business:')) {
      const accId = vision.split(':')[1];
      const acc = userAccounts?.find(a => a.account_id === accId);
      return acc?.name || 'Business';
    }
    return 'Pessoal';
  }, [vision, user, userAccounts]);

  // Dark theme
  useEffect(() => {
    document.body.classList.toggle('dark-theme', isDark);
    document.querySelector('.App')?.classList.toggle('dark-theme', isDark);
    localStorage.setItem('wf_theme', isDark ? 'dark' : 'light');
    // Aplica o tema de cor customizado, se existir
    const customAccent = localStorage.getItem('wf_accent_color');
    if (customAccent) {
      document.documentElement.style.setProperty('--accent', customAccent);
      document.documentElement.style.setProperty('--accent-hover', `${customAccent}dd`); // hover com leve opacidade
    }
  }, [isDark]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const addToast = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  const confirm = useCallback((message) =>
    new Promise(resolve =>
      setConfirmModal({ message, onConfirm: () => { setConfirmModal(null); resolve(true); }, onCancel: () => { setConfirmModal(null); resolve(false); } })
    ), []);

  const setQuickFilter = (type, status = 'todos') => {
    setFilterType(type);
    setFilterStatus(status);
    // Scroll suave até a tabela
    document.querySelector('.transactions-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const confirmAction = useCallback((message, onConfirm) => {
    setConfirmModal({
      message,
      onConfirm: () => { onConfirm(); setConfirmModal(null); },
      onCancel: () => setConfirmModal(null)
    });
  }, []);

  // Fetch de relatório via JWT (sem adminToken)
  const fetchReportData = useCallback(async (customDates = null) => {
    try {
      setLoading(true);
      setError(null);
      // Usa datas passadas ou o estado atual
      const dFrom = customDates?.start || dateFrom;
      const dTo   = customDates?.end   || dateTo;
      
      const start = dFrom.replace(/-/g, '');
      const end   = dTo.replace(/-/g, '');
      const res   = await authFetch(`/api/reports?start=${start}&end=${end}&vision=${visionMode}${visionAccountId ? '&account_id=' + visionAccountId : ''}`);
      if (!res.ok) {
        if (res.status === 403 && visionMode === 'business') {
          // Fallback se o usuário tentar acessar uma conta do qual não faz mais parte, ou por poluição de cache
          localStorage.removeItem('wf_vision');
          setVision('personal');
          throw new Error('Acesso negado à conta business. Retornando para visão Pessoal...');
        }
        throw new Error(`HTTP ${res.status}`);
      }
      const data  = await res.json();

      const normalize = (t) => ({
        id:          t.id,
        description: t.description || t.descrição || '',
        value:       parseFloat(t.value || t.valor || t.Valor) || 0,
        type:        t.type   || t.operacao || '',
        status:      t.status || '',
        category:    t.category || t.categoria || '',
        date:        t.date   || t.data || '',
        nome_usuario: t.nome_usuario,
        account_name:  t.account_name || t.nome_business,
        business_id:   t.business_id,
        comprovante_url: t.comprovante_url,
        cartao_id:    t.cartao_id,
        fatura_id:    t.fatura_id,
        recorrente:   t.recorrente,
        frequencia:   t.frequencia,
        recorrencia_id: t.recorrencia_id,
        recorrencia_total: t.recorrencia_total,
        recorrencia_numero: t.recorrencia_numero,
      });

      setReportData({
        ...data,
        transactions:    data.transactions.map(normalize),
        allTransactions: (data.allTransactions || data.transactions).map(normalize),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [visionMode, visionAccountId, authFetch, dateFrom, dateTo]);

  // ── Alertas de vencimento próximo (Todos Pendentes) ────────────────────────
  const pendingPayments = useMemo(() => {
    return (reportData?.transactions || []).filter(t => {
      const tStatus = (t.status || '').toLowerCase();
      return tStatus !== 'pago' && tStatus !== 'recebido';
    });
  }, [reportData?.transactions]);

  // ── Dashboard Alerts Animation ──────────────────────────────────────────
  const [animateAlert, setAnimateAlert] = useState(false);
  useEffect(() => {
    if (pendingPayments.length > 0) {
      const trigger = () => {
        setAnimateAlert(true);
        setTimeout(() => setAnimateAlert(false), 3000);
      };
      trigger();
      const interval = setInterval(trigger, 180000); // 3 minutes
      return () => clearInterval(interval);
    }
  }, [pendingPayments.length]);

  // Busca apenas no mount ou troca de visão
  // Não incluímos dateFrom/dateTo aqui para evitar o "reload" chato ao mexer no input
  useEffect(() => { 
    fetchReportData(); 
  }, [vision]);

  // Busca convites pendentes
  useEffect(() => {
    if (isAuthenticated) {
      authFetch('/api/business/invites')
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            setPendingInvites(data);
            setShowInviteModal(true);
          }
        })
        .catch(err => console.error('Erro ao buscar convites:', err));
    }
  }, [isAuthenticated, authFetch]);

  const handleRespondInvite = async (inviteId, action) => {
    try {
      const res = await authFetch(`/api/business/invites/${inviteId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast(`Convite ${action === 'aceitar' ? 'aceito' : 'recusado'} com sucesso!`);
      // Remove da lista pendente
      const newInvites = pendingInvites.filter(i => i.id !== inviteId);
      setPendingInvites(newInvites);
      if (newInvites.length === 0) setShowInviteModal(false);
      
      // Se aceitou, recarrega a página para atualizar o contexto do usuário (ou força update)
      if (action === 'aceitar') {
        window.location.reload();
      }
    } catch (err) {
      addToast(`Erro ao ${action} convite: ` + err.message, 'error');
    }
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const addTransaction = async (td) => {
    try {
      const res = await authFetch('/api/transactions', {
        method: 'POST',
        body: JSON.stringify({
          data:      td.date || new Date().toISOString().slice(0, 10),
          descricao: td.description || 'Nova Transacao',
          valor:     parseFloat(td.value) || 0,
          categoria: td.category || 'Outros',
          operacao:  td.type   || 'Despesa',
          status:    td.status || 'Pago',
          chat_id:   'web_user',
          nome_usuario: user?.name || 'Usuario Web',
          // [INFO] nome_business removido - dado legado, nao enviado mais
          business_id: td.business_id || (visionMode === 'business' ? visionAccountId : undefined),
          recorrente:   td.recorrente || false,
          frequencia:   td.frequencia || null,
          recorrencia_total: td.recorrencia_total || null,
          recorrencia_numero: td.recorrencia_numero || null,
          observacoes:  td.observacoes || null,
          tags:         td.tags || null,
          comprovante_url: td.comprovante_url || null,
          cartao_id:    td.cartao_id || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast('Transação criada com sucesso!');
      fetchReportData();
      setShowAddForm(false);
    } catch (err) {
      addToast('Erro ao criar: ' + err.message, 'error');
    }
  };

  const updateTransaction = async (id, td) => {
    try {
      const res = await authFetch(`/api/transactions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          data:      td.date || new Date().toISOString().slice(0, 10),
          descrição: td.description || 'Transação',
          valor:     parseFloat(td.value) || 0,
          categoria: td.category || 'Outros',
          operacao:  td.type   || 'Despesa',
          status:    td.status || 'Pago',
          recorrente:   td.recorrente,
          frequencia:   td.frequencia,
          recorrencia_total: td.recorrencia_total,
          recorrencia_numero: td.recorrencia_numero,
          recorrencia_id: td.recorrencia_id,
          observacoes:  td.observacoes,
          tags:         td.tags,
          business_id:  td.business_id || undefined,
          nome_business: td.nome_business || undefined,
          comprovante_url: td.comprovante_url || null,
          cartao_id:    td.cartao_id || null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast('Transação atualizada!');
      fetchReportData();
      setEditingTx(null);
    } catch (err) {
      addToast('Erro ao atualizar: ' + err.message, 'error');
    }
  };

  const deleteTransaction = async (tx) => {
    // Se tx for string (ID), buscamos o objeto (compatibilidade com chamadas antigas se houver)
    const transaction = typeof tx === 'object' ? tx : reportData.allTransactions.find(t => t.id === tx);
    if (!transaction) return;

    if (transaction.recorrente) {
      setRecurringDeleteModal({
        tx: transaction,
        onConfirmOnly: () => performDelete(transaction.id, false),
        onConfirmAll: () => performDelete(transaction.id, true),
        onCancel: () => setRecurringDeleteModal(null)
      });
      return;
    }

    const ok = await confirm('Tem certeza que deseja excluir esta transação?');
    if (!ok) return;
    performDelete(transaction.id, false);
  };

  const performDelete = async (id, all = false) => {
    try {
      setRecurringDeleteModal(null);
      const url = `/api/transactions/${id}${all ? '?all=true' : ''}`;
      const res = await authFetch(url, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      addToast(all ? 'Série de transações excluída!' : 'Transação excluída!');
      fetchReportData();
    } catch (err) {
      addToast('Erro ao excluir: ' + err.message, 'error');
    }
  };

  const duplicateTransaction = async (t) => {
    try {
      const res = await authFetch('/api/transactions', {
        method: 'POST',
        body: JSON.stringify({
          data:      new Date().toISOString().slice(0, 10),
          descricao: `(Copia) ${t.description}`,
          valor:     parseFloat(t.value) || 0,
          categoria: t.category || 'Outros',
          operacao:  t.type   || 'Despesa',
          status:    'A Pagar',
          chat_id:   'web_user',
          nome_usuario: user?.name || 'Usuario Web',
          // nome_business removido
          business_id: t.business_id || undefined,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast('Transação duplicada!');
      fetchReportData();
    } catch (err) {
      addToast('Erro ao duplicar: ' + err.message, 'error');
    }
  };

  const updateTransactionDate = async (id, newDate) => {
    try {
      // Pega a transação original para re-enviar os dados (já que a API espera o objeto completo pelo PUT)
      const t = reportData.allTransactions.find(x => x.id === id);
      if (!t) return;
      const res = await authFetch(`/api/transactions/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          data:      newDate,
          descrição: t.description,
          valor:     parseFloat(t.value) || 0,
          categoria: t.category || 'Outros',
          operacao:  t.type || 'Despesa',
          status:    t.status || 'Pago',
          business_id:  t.business_id || undefined,
          nome_business: t.nome_business || undefined
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast('Data atualizada!');
      fetchReportData();
    } catch (err) {
      addToast('Erro ao atualizar data: ' + err.message, 'error');
    }
  };

  const toggleTransactionStatus = async (t) => {
    try {
      const isPaid = ['pago', 'recebido'].includes(t.status.toLowerCase());
      const newStatus = isPaid ? 'Pendente' : (['receita', 'entrada'].includes(t.type.toLowerCase()) ? 'Recebido' : 'Pago');
      
      const res = await authFetch(`/api/transactions/${t.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          data:      t.date,
          descrição: t.description,
          valor:     parseFloat(t.value) || 0,
          categoria: t.category || 'Outros',
          operacao:  t.type || 'Despesa',
          status:    newStatus,
          business_id:  t.business_id || undefined,
          nome_business: t.nome_business || undefined,
          tags:      t.tags,
          comprovante_url: t.comprovante_url,
          recorrente: t.recorrente,
          recorrencia_id: t.recorrencia_id
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast(`Status alterado para ${newStatus}!`);
      fetchReportData();
    } catch (err) {
      addToast('Erro ao alterar status: ' + err.message, 'error');
    }
  };

  const quickAddTransaction = async (desc, val, type, date) => {
    try {
      const res = await authFetch('/api/transactions', {
        method: 'POST',
        body: JSON.stringify({
          data:      date,
          descricao: desc,
          valor:     parseFloat(val) || 0,
          categoria: 'Outros',
          operacao:  type,
          status:    'Pago',
          chat_id:   'web_user',
          nome_usuario: user?.name || 'Usuario Web',
          // nome_business removido
          business_id: visionMode === 'business' ? visionAccountId : undefined,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      addToast('Lançamento adicionado!');
      fetchReportData();
    } catch (err) {
      addToast('Erro ao adicionar: ' + err.message, 'error');
    }
  };

  // ── Filtros client-side ────────────────────────────────────────────────────
  const filteredTransactions = (reportData?.transactions || []).filter(t => {
    const matchSearch = !debouncedSearch ||
      (t.description || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (t.category    || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (t.tags        || '').toLowerCase().includes(debouncedSearch.toLowerCase());
    const matchType   = filterType   === 'todos' || (t.type  || '').toLowerCase() === filterType;
    
    // Tratativa inteligente de Status (agrupando 'recebido/pago' X 'pendente/a pagar')
    const tStatus = (t.status || '').toLowerCase();
    const isEfetivado = tStatus === 'pago' || tStatus === 'recebido';
    let matchStatus = true;
    if (filterStatus === 'pago') matchStatus = isEfetivado;
    if (filterStatus === 'pendente') matchStatus = !isEfetivado;

    return matchSearch && matchType && matchStatus;
  });

  // ── Charts ─────────────────────────────────────────────────────────────────
  const getChartData = () => {
    if (!reportData) return null;
    const { balances, transactions } = reportData;

    const incomeExpenseData = {
      labels: ['Recebidos', 'A Receber', 'Despesas', 'A Pagar'],
      datasets: [{
        label: 'Valores (R$)',
        data: [balances.received, balances.toReceive, balances.expenses, balances.toPay],
        backgroundColor: [
          'rgba(59,130,246,0.85)', 'rgba(59,130,246,0.45)',
          'rgba(239,68,68,0.85)', 'rgba(239,68,68,0.45)',
        ],
        borderRadius: 6,
        barPercentage: 0.5,
        categoryPercentage: 0.5
      }],
    };

    const catMap = {};
    transactions.forEach(t => {
      const cat = t.category || 'Outros';
      const isInc = ['receita','entrada'].includes((t.type||'').toLowerCase());
      const typeKey = isInc ? 'receita' : 'despesa';
      if (!catMap[cat]) catMap[cat] = { total: 0, type: typeKey };
      catMap[cat].total += parseFloat(t.value) || 0;
    });
    
    const recColors = ['#2E86DE', '#1ABC9C', '#27AE60'];
    const expColors = ['#E74C3C', '#C0392B', '#D35400'];
    
    let recCount = 0;
    let expCount = 0;
    
    const cats = Object.keys(catMap);
    const backgroundColors = cats.map(cat => {
      if (catMap[cat].type === 'receita') {
        return recColors[recCount++ % recColors.length];
      }
      return expColors[expCount++ % expColors.length];
    });

    const categoryData = {
      labels: cats,
      datasets: [{ data: cats.map(cat => catMap[cat].total), backgroundColor: backgroundColors, borderWidth: 0 }],
    };

    // Gráfico de evolução mensal
    const monthMap = {};
    (reportData.allTransactions || transactions).forEach(t => {
      if (!t.date) return;
      const mo = t.date.slice(0, 7); // YYYY-MM
      if (!monthMap[mo]) monthMap[mo] = { rec: 0, des: 0 };
      const v = parseFloat(t.value) || 0;
      const isInc = ['receita','entrada'].includes((t.type||'').toLowerCase());
      if (isInc) monthMap[mo].rec += v;
      else       monthMap[mo].des += v;
    });
    const months = Object.keys(monthMap).sort();
    const monthLabels = months.map(m => {
      const [y, mo] = m.split('-');
      return `${mo}/${y.slice(2)}`;
    });
    const evolutionData = {
      labels: monthLabels,
      datasets: [
        { label: 'Receitas',  data: months.map(m => monthMap[m].rec), borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)', tension: 0.4, fill: true },
        { label: 'Despesas',  data: months.map(m => monthMap[m].des), borderColor: '#ef4444', backgroundColor: 'rgba(239,68,68,0.1)',   tension: 0.4, fill: true },
      ],
    };

    return { incomeExpenseData, categoryData, evolutionData };
  };

  const chartData = getChartData();

  const handleExport = (type) => {
    if (!filteredTransactions || filteredTransactions.length === 0) return addToast('Nenhum dado para exportar', 'info');
    // Resolve o nome correto da conta ativa (usa o retorno do backend como fonte primária)
    let activeAccountName = user?.name || 'Pessoal';
    let accountTypeLabel  = 'Pessoal';
    if (visionMode === 'business') {
      // Tenta usar o nome retornado diretamente pelo backend
      const nameFromApi = reportData?.accountName;
      const nameFromList = (userAccounts || []).find(a => a.account_id === visionAccountId)?.name;
      activeAccountName = nameFromApi || nameFromList || 'Business';
      accountTypeLabel  = 'Conta Business';
    }
    const exportUser = { ...user, name: activeAccountName, accountType: accountTypeLabel };
    if (type === 'csv') { exportToCSV(filteredTransactions, reportData.balances, reportData.period, exportUser); addToast('Extrato CSV exportado!'); }
    else                { exportToPDF(filteredTransactions, reportData.balances, reportData.period, exportUser); addToast('Extrato PDF exportado!'); }
  };

  const allFormTags = useMemo(() => {
    if (!reportData?.allTransactions) return [];
    const set = new Set();
    reportData.allTransactions.forEach(t => {
      if (t.tags) t.tags.split(',').forEach(tag => set.add(tag.trim().toLowerCase()));
    });
    return Array.from(set).filter(Boolean).sort();
  }, [reportData]);

  if (loading && !reportData) return (
    <div className="loading-container">
      <div className="loading-spinner" />
      <span className="loading-text">Carregando dados financeiros...</span>
    </div>
  );

  if (error) return (
    <div className="error-container">
      <div className="error-icon">⚠️</div>
      <div className="error-title">Erro ao carregar dados</div>
      <div className="error-msg">{error}</div>
      <button className="error-btn" onClick={fetchReportData}>Tentar novamente</button>
    </div>
  );

  return (
    <div className={`App ${isDark ? 'dark-theme' : ''}`}>
      {sessionExpired && (
        <ConfirmModal 
          message="Sua sessão expirou por inatividade. Por favor, faça login novamente para continuar." 
          onConfirm={() => { setSessionExpired(false); window.location.reload(); }} 
          onCancel={() => { setSessionExpired(false); window.location.reload(); }}
        />
      )}

      <Header
        title="Walco Finance"
        subtitle="Dashboard Financeiro"
        onAddTransaction={() => { setInitialTxDate(null); setShowAddForm(true); }}
        onToggleDarkTheme={() => setIsDark(d => !d)}
        isDark={isDark}
        onOpenCategories={() => setShowCategories(true)}
        onOpenGoals={() => setShowGoals(true)}
        onOpenBudgets={() => setShowBudgets(true)}
        onOpenBusiness={() => setShowBusiness(true)}
        onOpenCards={() => setShowCards(true)}
        onOpenReports={() => setShowCustomReports(true)}
        onOpenHelp={() => setShowHelp(true)}
        onExport={handleExport}
        onImport={() => setShowImport(true)}
        onOpenProfile={() => setShowProfile(true)}
        vision={vision}
        setVision={setVision}
      />

      <main>
        {/* ── Alertas de vencimento ── */}
        {pendingPayments.length > 0 && (() => {
          const upcomingIncome = pendingPayments.filter(t => ['receita','entrada'].includes((t.type||'').toLowerCase()));
          const upcomingExpense = pendingPayments.filter(t => !['receita','entrada'].includes((t.type||'').toLowerCase()));
          const incomeTotal = upcomingIncome.reduce((acc, t) => acc + (parseFloat(t.value) || 0), 0);
          const expenseTotal = upcomingExpense.reduce((acc, t) => acc + (parseFloat(t.value) || 0), 0);
          
          return (
            <div 
              className={`upcoming-alert ${animateAlert ? 'attention-animate' : ''}`}
              onClick={() => document.getElementById('transactions-list')?.scrollIntoView({ behavior: 'smooth' })}
              style={{ cursor: 'pointer' }}
              title="Clique para ver os lançamentos"
            >
              <div className="alert-header-mobile" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <span>🔔</span>
                <strong className="alert-title-mobile">Lançamentos Pendentes:</strong>
              </div>
              {upcomingIncome.length > 0 && (
                <div className="upcoming-item income" style={{ color: '#10b981', background: 'rgba(16, 185, 129, 0.15)' }}>
                  📈 ({upcomingIncome.length}) +{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(incomeTotal)}
                </div>
              )}
              {upcomingExpense.length > 0 && (
                <div className="upcoming-item expense" style={{ color: '#ef4444', background: 'rgba(239, 68, 68, 0.15)' }}>
                  📉 ({upcomingExpense.length}) -{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(expenseTotal)}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Filtro de data ── */}
        <div className="date-filters-container">
          <div className="date-filters-header-row">
            <span className="date-filter-label">Período</span>
            {isAuthenticated && (
              <div className="mobile-account-selector">
                <span className="account-label">Conta:</span>
                <select
                  className="vision-select-filter"
                  value={vision}
                  onChange={(e) => {
                    const val = e.target.value;
                    setVision(val);
                    localStorage.setItem('wf_vision', val);
                    if (val.startsWith('business:')) {
                      setActiveAccount(val.split(':')[1]);
                    } else {
                      setActiveAccount(null);
                    }
                  }}
                >
                  <option value="personal">
                    {'\u{1F464}'} {formatDisplayName(user?.name || 'Pessoal')}
                  </option>
                  {/* Novo modelo: lista todas as contas business */}
                  {(userAccounts || []).length > 0 ? (
                    (userAccounts || []).map(acc => (
                      <option key={acc.account_id} value={`business:${acc.account_id}`}>
                        {'\u{1F3E2}'} {formatDisplayName(acc.name || 'Business')}
                        {acc.role === 'owner' ? ' \u2605' : ''}
                      </option>
                    ))
                  ) : (
                    /* Fallback legado: usa user.business_id se account_users nao populado ainda */
                    user?.business_id && (
                      <option value={`business:${user.business_id}`}>
                        {'\u{1F3E2}'} {formatDisplayName(user.nome_business || 'Business')}
                      </option>
                    )
                  )}
                </select>
              </div>
            )}
          </div>
          
          <div className="date-filters">
            <div className="date-input-group">
              <input type="date" className="date-filter-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            
            <div className="date-input-separator-wrapper">
              <span className="date-filter-separator">até</span>
            </div>

            <div className="date-input-group">
              <input type="date" className="date-filter-input" value={dateTo}   onChange={e => setDateTo(e.target.value)} />
            </div>

            <div className="date-input-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button className="date-filter-btn" onClick={fetchReportData}>Filtrar</button>
              {loading && <span className="refreshing-mini-loader" title="Atualizando dados...">🔄</span>}
            </div>
          </div>
        </div>

        <div className="dashboard-container">
          {/* ── KPIs ── */}
          {reportData && (
            <div className="kpis-grid">
              <KpiCard title="Saldo Anterior"  value={reportData.balances.previous}  isCurrency />
              <KpiCard title="Recebidos"       value={reportData.balances.received}  isCurrency isPositive onClick={() => setQuickFilter('receita', 'pago')} />
              <KpiCard title="A Receber"       value={reportData.balances.toReceive} isCurrency isPositive onClick={() => setQuickFilter('receita', 'a pagar')} />
              <KpiCard title="Despesas"        value={reportData.balances.expenses}  isCurrency isNegative onClick={() => setQuickFilter('despesa', 'pago')} />
              <KpiCard title="A Pagar"         value={reportData.balances.toPay}     isCurrency isNegative onClick={() => setQuickFilter('despesa', 'a pagar')} />
              <KpiCard title="Saldo Previsto"  value={reportData.balances.projected} isCurrency />
              <KpiCard title="Saldo Real"      value={reportData.balances.actual}    isCurrency isEmphasized onClick={() => setQuickFilter('todos', 'todos')} />
            </div>
          )}

          {/* ── Seção de Gráficos Opcional ── */}
          {chartData && (
            <div className={`charts-collapsible-section ${showCharts ? 'expanded' : ''}`}>
              <div className="section-collapsible-header" onClick={() => setShowCharts(!showCharts)}>
                <div className="section-title">
                  <span>📈</span>
                  <h2 className="cat-title-small">Visualização Gráfica</h2>
                </div>
                <button className={`toggle-btn ${showCharts ? 'active' : ''}`}>
                  {showCharts ? '▲ Ocultar' : '▼ Mostrar'}
                </button>
              </div>
              
              <div className="charts-collapsible-content">
                <div className="charts-grid">
                  <FinancialChart type="bar"  data={chartData.incomeExpenseData} title="Receitas vs Despesas" />
                  <FinancialChart type="pie"  data={chartData.categoryData}      title="Por Categoria" />
                </div>

                {chartData?.evolutionData?.labels?.length > 1 && (
                  <div className="evolution-chart">
                    <FinancialChart type="line" data={chartData.evolutionData} title="Evolução Mensal" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Relatório por Categoria */}
          {reportData && (
            <CategoryReport transactions={reportData.transactions} />
          )}

          {/* Calendário Financeiro */}
          {reportData && (
            <FinancialCalendar 
              transactions={reportData.allTransactions || reportData.transactions} 
              onDayClick={(dateStr) => { setInitialTxDate(dateStr); setShowAddForm(true); }}
              onUpdateTransactionDate={updateTransactionDate}
            />
          )}

          {/* ── Resumo por Ano ── */}
          {reportData && (
            <YearSummary transactions={reportData.allTransactions || reportData.transactions} />
          )}

          <div className="transactions-section" id="transactions-list">
            <div className="transactions-section-header">
              <h2>Transações do Período ({filteredTransactions.length})</h2>
              <div className="filters-bar">
                <div className="filter-search">
                  <span>🔍</span>
                  <input
                    type="text"
                    placeholder="Buscar descrição, categoria ou tags..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="filters-select-row">
                  <select className="filter-select" value={filterType}   onChange={e => setFilterType(e.target.value)}>
                    <option value="todos">Todos os tipos</option>
                    <option value="receita">Receita</option>
                    <option value="despesa">Despesa</option>
                  </select>
                  <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="todos">Todos os status</option>
                    <option value="pago">✅ Pago</option>
                    <option value="pendente">⏳ Pendente</option>
                  </select>
                </div>
              </div>
            </div>
            <TransactionsTable
              transactions={filteredTransactions}
              onEdit={setEditingTx}
              onDelete={deleteTransaction}
              onDuplicate={duplicateTransaction}
              onAdd={() => { setInitialTxDate(null); setShowAddForm(true); }}
              onToggleStatus={toggleTransactionStatus}
            />
          </div>
        </div>
      </main>

      {showAddForm   && <TransactionForm addToast={addToast} initialDate={initialTxDate} vision={vision} allTags={allFormTags} onSubmit={addTransaction} onCancel={() => setShowAddForm(false)} />}
      {editingTx     && <TransactionForm addToast={addToast} transaction={editingTx} vision={vision} allTags={allFormTags} onSubmit={(d) => updateTransaction(editingTx.id, d)} onCancel={() => setEditingTx(null)} />}
      {showCategories && <CategoryManager onClose={() => setShowCategories(false)} />}
      {showGoals     && <GoalsManager    onClose={() => setShowGoals(false)} />}
      {showBudgets   && <BudgetManager   onClose={() => setShowBudgets(false)} transactions={reportData?.transactions} />}
      {showImport    && <ImportTransactions onClose={() => setShowImport(false)} onImportComplete={fetchReportData} />}
      {showBusiness   && <BusinessManager    onClose={() => setShowBusiness(false)} />}
      {showProfile   && <UserProfile
        onClose={() => setShowProfile(false)}
        vision={vision}
        accountName={currentAccountName}
        addToast={addToast}
        confirmAction={confirmAction}
      />}
      {showCards     && <CreditCards     onClose={() => setShowCards(false)} />}
      {showCustomReports && <CustomReports
        onClose={() => setShowCustomReports(false)}
        addToast={addToast}
        confirmAction={confirmAction}
      />}
      {showHelp      && <HelpModal       onClose={() => setShowHelp(false)} />}
      {confirmModal  && <ConfirmModal {...confirmModal} />}
      {recurringDeleteModal && <RecurringDeleteModal {...recurringDeleteModal} />}
      {showInviteModal && pendingInvites.length > 0 && (
        <div className="modal-overlay">
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">🏢</div>
            <h3 style={{ margin: '0 0 10px' }}>Convite de Conta Business</h3>
            <p className="confirm-message">Você foi convidado para participar de: <strong>{pendingInvites[0].nome_business}</strong></p>
            <div className="confirm-actions">
              <button className="modal-btn modal-btn-cancel" onClick={() => handleRespondInvite(pendingInvites[0].id, 'recusar')}>Recusar</button>
              <button className="modal-btn modal-btn-confirm" onClick={() => handleRespondInvite(pendingInvites[0].id, 'aceitar')}>Aceitar Convite</button>
            </div>
          </div>
        </div>
      )}
      <Toast toasts={toasts} />
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────
function AppContent() {
  const { isAuthenticated, loading, sessionExpired, setSessionExpired } = useAuth();
  
  const urlParams = new URLSearchParams(window.location.search);
  const isConfirmDeleteMode = urlParams.get('confirm-delete') === 'true' && urlParams.get('token');

  if (loading) return <div className="loading-container"><div className="loading-spinner" /></div>;
  
  if (isConfirmDeleteMode) return <AuthPage />;
  
  return isAuthenticated ? <Dashboard sessionExpired={sessionExpired} setSessionExpired={setSessionExpired} /> : <AuthPage />;
}

function App() {
  return <AuthProvider><AppContent /></AuthProvider>;
}

export default App;
